import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { RestaurantAttendance, RestaurantAttendanceCell, RestaurantAttendanceGrid as Grid,
  RestaurantAttendanceRow } from '../types/restaurantPersonnel';
import { extractApiErrorMessage } from '../utils/apiError';
import { formatAttendanceIntervalHours, formatCivilDate, formatCivilDateTime, formatHours } from '../utils/restaurantPersonnelFormat';
import RestaurantAttendanceEditor from './RestaurantAttendanceEditor';
import { currentRestaurantMonth, isRestaurantMonth } from '../utils/restaurantMonth';

const MIN_MONTH = '2000-01';
const MAX_MONTH = '2200-12';
const REOPEN_AFTER_REFRESH = 'Дані змінено. Після оновлення відкрийте потрібну клітинку знову.';
const shiftMonth = (value: string, delta: number) => {
  const [year, number] = value.split('-').map(Number);
  const index = year * 12 + number - 1 + delta;
  return `${Math.floor(index / 12)}-${String(index % 12 + 1).padStart(2, '0')}`;
};
const RestaurantAttendanceGrid: React.FC<{
  canEdit: boolean;
  refreshKey: number;
  onSaved: () => void;
  initialMonth?: string;
}> = ({ canEdit, refreshKey, onSaved, initialMonth }) => {
  const [month, setMonth] = useState(() => isRestaurantMonth(initialMonth) ? initialMonth : currentRestaurantMonth());
  const [departmentId, setDepartmentId] = useState('');
  const [departments, setDepartments] = useState<Grid['departments']>([]);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState<Grid | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [appliedRefreshKey, setAppliedRefreshKey] = useState(refreshKey);
  const [selected, setSelected] = useState<{ row: RestaurantAttendanceRow; cell: RestaurantAttendanceCell } | null>(null);
  const [original, setOriginal] = useState<RestaurantAttendance | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const dirtyRef = useRef(false);
  const markDirty = useCallback((value: boolean) => {
    dirtyRef.current = value;
    setDirty(value);
  }, []);
  const requestKey = `${month}|${departmentId}|${search}|${page}|${pageSize}|${reload}|${appliedRefreshKey}`;
  const [loadedKey, setLoadedKey] = useState('');
  const ready = loadedKey === requestKey;

  useEffect(() => {
    if (!dirty) setAppliedRefreshKey(refreshKey);
  }, [dirty, refreshKey]);
  useEffect(() => {
    if (!canEdit || !selected) markDirty(false);
  }, [canEdit, selected, markDirty]);

  useEffect(() => {
    const controller = new AbortController();
    setResult(null);
    setSelected(null);
    setError('');
    const [year, number] = month.split('-').map(Number);
    void api.get<Grid>('/restaurant-personnel/attendance-grid', {
      signal: controller.signal, params: { year, month: number, departmentId: departmentId || undefined, search, page, pageSize },
    }).then(response => {
      if (controller.signal.aborted) return;
      setResult(response.data);
      setDepartments(response.data.departments);
      setLoadedKey(requestKey);
    }).catch(failure => {
      if (controller.signal.aborted) return;
      // Departments are month-specific; fall back to all departments instead of showing a dead end.
      if (failure?.response?.status === 404 && departmentId) {
        setNotice('Обраного підрозділу немає в цьому місяці; показано всі підрозділи.');
        setDepartmentId('');
        return;
      }
      setError(extractApiErrorMessage(failure, 'Не вдалося завантажити місячний табель.'));
    });
    return () => controller.abort();
  }, [month, departmentId, search, page, pageSize, reload, appliedRefreshKey, requestKey]);

  useEffect(() => { if (selected) heading.current?.focus(); }, [selected]);
  const discardDraft = () => {
    if (dirtyRef.current && !window.confirm('Є незбережені зміни інтервалу. Відкинути їх і продовжити?'))
      return false;
    markDirty(false);
    setAppliedRefreshKey(refreshKey);
    return true;
  };
  const chooseEntry = (item: RestaurantAttendance | null) => {
    const refreshPending = refreshKey !== appliedRefreshKey;
    if (!discardDraft()) return false;
    if (refreshPending) {
      setSelected(null);
      setNotice(REOPEN_AFTER_REFRESH);
      return false;
    }
    setNotice(current => current === REOPEN_AFTER_REFRESH ? '' : current);
    setOriginal(item);
    setEditorKey(value => value + 1);
    return true;
  };
  const rows = ready ? result?.employees.items ?? [] : [];
  const totals = rows.reduce((sum, row) => ({
    hours: sum.hours + row.hoursWorked, weighted: sum.weighted + row.weightedHours,
  }), { hours: 0, weighted: 0 });
  const pages = result ? Math.max(1, Math.ceil(result.employees.total / pageSize)) : 1;
  const overall = ready ? result?.totals : undefined;
  // A shrinking filter can leave the current page beyond the end; return to the last real page.
  useEffect(() => {
    if (ready && result && page > pages) setPage(pages);
  }, [ready, result, page, pages]);
  const goToMonth = (delta: number) => {
    const next = shiftMonth(month, delta);
    if (next < MIN_MONTH || next > MAX_MONTH) return;
    if (!discardDraft()) return;
    setMonth(next); setPage(1); setNotice('');
  };
  const moveFocus = (event: React.KeyboardEvent<HTMLTableSectionElement>) => {
    const step = { ArrowLeft: [0, -1], ArrowRight: [0, 1], ArrowUp: [-1, 0], ArrowDown: [1, 0] }[event.key];
    const current = (event.target as HTMLElement).closest('button[data-cell]');
    if (!step || !current) return;
    const [row, col] = (current.getAttribute('data-cell') ?? '').split(':').map(Number);
    let nextRow = row + step[0];
    let nextCol = col + step[1];
    while (nextRow >= 0 && nextRow < rows.length && nextCol >= 0 && nextCol < (result?.days.length ?? 0)) {
      const next = event.currentTarget.querySelector<HTMLButtonElement>(
        `button[data-cell="${nextRow}:${nextCol}"]:not(:disabled)`);
      if (next) { event.preventDefault(); next.focus(); return; }
      nextRow += step[0];
      nextCol += step[1];
    }
  };

  return <section aria-label="Місячний табель" className="restaurant-attendance-grid">
    <h2>Місячний табель працівників</h2>
    <details>
      <summary>Детальніше про дати та підрахунок табеля</summary>
      <p>Робоча дата: від першого числа включно до наступного місяця виключно.
        Відкриті інтервали позначено окремо; години не дораховуються автоматично.</p>
    </details>
    <div className="restaurant-attendance-grid__months">
      <button type="button" disabled={saving || month <= MIN_MONTH} onClick={() => goToMonth(-1)}>← Попередній місяць</button>
      <strong aria-live="polite">{month}</strong>
      <button type="button" disabled={saving || month >= MAX_MONTH} onClick={() => goToMonth(1)}>Наступний місяць →</button>
    </div>
    <div className="restaurant-personnel__form">
      <label>Місяць таблиці<input type="month" required min={MIN_MONTH} max={MAX_MONTH} value={month} disabled={saving}
        onChange={event => {
          if (/^(20\d{2}|21\d{2}|2200)-(0[1-9]|1[0-2])$/.test(event.target.value) &&
              event.target.value !== month && discardDraft()) {
            setMonth(event.target.value); setPage(1); setNotice('');
          }
        }} /></label>
      <label>Підрозділ табелю<select value={departmentId} disabled={saving} onChange={event => {
        if (event.target.value !== departmentId && discardDraft()) {
          setDepartmentId(event.target.value); setPage(1);
        }
      }}>
        <option value="">Усі підрозділи</option>
        {departments.map(unit => <option key={unit.departmentId} value={unit.departmentId}>{unit.name}</option>)}
      </select></label>
      <label>Працівників на сторінці<select value={pageSize} disabled={saving} onChange={event => {
        const next = Number(event.target.value);
        if (next !== pageSize && discardDraft()) { setPageSize(next); setPage(1); }
      }}>{[10, 25, 50, 100].map(size => <option key={size}>{size}</option>)}</select></label>
    </div>
    <form onSubmit={event => {
      event.preventDefault();
      const next = query.trim();
      if ((next !== search || page !== 1) && discardDraft()) { setSearch(next); setPage(1); }
    }}>
      <label>Пошук у табелі<input type="search" maxLength={200} value={query} disabled={saving}
        onChange={event => setQuery(event.target.value)} /></label>
      <div className="restaurant-personnel__actions">
        <button disabled={saving} type="submit">Знайти в табелі</button>
        <button disabled={saving} type="button" onClick={() => {
          if (discardDraft()) setReload(value => value + 1);
        }}>Оновити місячний табель</button>
      </div>
    </form>
    {error && <p role="alert">{error}</p>}
    {notice && !error && <p role="status">{notice}</p>}
    {dirty && <p role="status">Є незбережені зміни інтервалу.</p>}
    {dirty && refreshKey !== appliedRefreshKey &&
      <p role="status">Табель змінено в іншій частині екрана. Збережіть або відкиньте чернетку, щоб оновити дані.</p>}
    {(!result || !ready) && !error && <p role="status">Завантаження місячного табелю…</p>}
    {ready && result && <>
      <p role="status">Працевлаштувань у табелі: {result.employees.total} · Сторінка {result.employees.page} з {pages}</p>
      {overall ? <section aria-label="Підсумки всього фільтра" className="restaurant-attendance-grid__totals">
        <h3>Підсумки всього фільтра (усі сторінки)</h3>
        <p>{formatHours(overall.hoursWorked)} фактичних · {formatHours(overall.weightedHours)} зарахованих
          (з коефіцієнтами) · інтервалів усього: {overall.intervals}</p>
        {overall.openIntervals > 0 && <p>Відкритих інтервалів: {overall.openIntervals} у працівників:
          {' '}{overall.employmentsWithOpenIntervals}; їхні години не включено до підсумків.</p>}
        {overall.attendanceDisabledEmployments > 0 &&
          <p>Табелювання вимкнено у працівників: {overall.attendanceDisabledEmployments}.</p>}
      </section> : <p role="alert">Підсумки всього фільтра недоступні; показано лише підсумки сторінки.</p>}
      <p>Підсумки цієї сторінки: {formatHours(totals.hours)} фактичних · {formatHours(totals.weighted)} зарахованих (з коефіцієнтами).</p>
      {!rows.length ? <p>Працевлаштувань за обраними умовами немає.</p> :
        <div className="restaurant-personnel__scroll" tabIndex={0} role="region" aria-label="Таблиця працівники та дні">
          <table>
            <caption>Табель {month} · у клітинках зараховані години; натисніть для перегляду інтервалів; стрілки переміщують між клітинками</caption>
            <thead><tr><th scope="col">Працівник</th>
              {result.days.map(day => <th scope="col" key={day}><time dateTime={day}>{day.slice(-2)}</time></th>)}
              <th scope="col">Фактичні, год</th><th scope="col">Зараховані, год</th>
            </tr></thead>
            <tbody onKeyDown={moveFocus}>{rows.map((row, rowIndex) => <tr key={row.employmentId}>
              <th scope="row" title={row.fullName}><span className="restaurant-attendance-grid__name">{row.fullName}</span>
                <small>{row.position} · {row.departmentName || 'Без підрозділу'}</small>
                {!row.useAttendance && <small>Табелювання вимкнено</small>}
                {row.cells.some(cell => cell.entries.some(item => !item.checkOutAt)) && <small>Є відкриті інтервали</small>}</th>
              {row.cells.map((cell, cellIndex) => {
                const open = cell.entries.some(item => !item.checkOutAt);
                return <td key={cell.workDate}>
                  <button type="button" data-cell={`${rowIndex}:${cellIndex}`}
                    disabled={saving || (!cell.employed && !cell.entries.length)}
                    aria-label={`${row.fullName}, ${formatCivilDate(cell.workDate)}: ${cell.entries.length} інтервалів${open ? ', відкрита зміна' : ''}${cell.entries.length ? `, зараховано ${formatHours(cell.weightedHours)}` : ''}`}
                    onClick={event => {
                      if (chooseEntry(null)) {
                        trigger.current = event.currentTarget;
                        setSelected({ row, cell });
                      }
                    }}>
                    {cell.entries.length ? formatHours(cell.weightedHours, 2, false) : cell.employed ? '—' : 'Не працює'}
                    {cell.entries.length > 1 && <small>{cell.entries.length} інтерв.</small>}
                    {open && <small>Відкрито</small>}
                  </button>
                </td>;
              })}
              <td>{formatHours(row.hoursWorked, 4, false)}</td><td>{formatHours(row.weightedHours, 4, false)}</td>
            </tr>)}</tbody>
          </table>
        </div>}
      <div className="restaurant-personnel__actions">
        <button type="button" disabled={saving || page === 1} onClick={() => {
          if (discardDraft()) setPage(page - 1);
        }}>Попередні працівники табелю</button>
        <button type="button" disabled={saving || page * pageSize >= result.employees.total}
          onClick={() => { if (discardDraft()) setPage(page + 1); }}>Наступні працівники табелю</button>
      </div>
    </>}
    {ready && selected && <section aria-label="Інтервали клітинки">
      <h3 ref={heading} tabIndex={-1}>{selected.row.fullName} · {formatCivilDate(selected.cell.workDate)}</h3>
      <p>Фактичні: {formatHours(selected.cell.hoursWorked)} · Зараховані: {formatHours(selected.cell.weightedHours)}</p>
      {!selected.cell.entries.length && <p>Інтервалів за цей день немає.</p>}
      <div className="restaurant-personnel__list">{selected.cell.entries.map(item =>
        <div key={item.id}>
          <p>{formatCivilDateTime(item.checkInAt)} — {item.checkOutAt ? formatCivilDateTime(item.checkOutAt) : 'Відкрито'}
            {' · '}{formatAttendanceIntervalHours(item)} · коеф. {item.hourCoefficient} · {item.comment}</p>
          {canEdit && selected.row.useAttendance && <button type="button" disabled={saving} aria-pressed={original?.id === item.id}
            title={`ID інтервалу: ${item.id}`} onClick={() => chooseEntry(item)}>Редагувати інтервал</button>}
        </div>)}</div>
      {canEdit && selected.row.useAttendance && (selected.cell.employed || original) &&
        <RestaurantAttendanceEditor key={editorKey} employmentId={selected.row.employmentId}
          original={original} defaultDate={selected.cell.workDate} onSavingChange={setSaving} onDirtyChange={markDirty}
          onDeleted={() => {
            markDirty(false); setAppliedRefreshKey(refreshKey); setSelected(null); setReload(value => value + 1); onSaved();
          }}
          onNew={selected.cell.employed ? () => chooseEntry(null) : undefined}
          onSaved={() => {
            markDirty(false); setAppliedRefreshKey(refreshKey); setSelected(null); setReload(value => value + 1); onSaved();
          }} />}
      {!selected.row.useAttendance && <p>Редагування недоступне: табелювання вимкнено у картці.</p>}
      <button type="button" disabled={saving} onClick={() => {
        if (discardDraft()) { setSelected(null); trigger.current?.focus(); }
      }}>Закрити клітинку</button>
    </section>}
  </section>;
};
export default RestaurantAttendanceGrid;
